import {
  Box,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import type { ReactNode } from 'react'

import { PulsingDot } from '@/apps/latest/components/atoms/PulsingDot'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { useReleaseChannel } from '@/features/auth'
import { FONT_WEIGHT } from '@/shared/styles/typography'
import type { YakurekiDetail } from '@/shared/types/yakurekiCore'

import { useGuidanceQueryV3 } from '../../hooks/queries/useGuidanceQueryV3'
import type { GuidanceSupportDataV3 } from '../../types'
import { GuidanceCheckPointList } from '../GuidanceCheckPointList'
import { GuidancePrepCard } from '../GuidancePrepCard'
import { KasanStatusPanel } from '../KasanStatusPanel'
import { TracingReportPrepRow } from './TracingReportPrepRow'
import { PrescriptionWithDiffPanel } from '../PrescriptionWithDiffPanel'
import { PreviousKeywordsPanel } from '../PreviousKeywordsPanel'

interface Props {
  event: YakurekiDetail
}

// 上位 section header. Notion spec (= docs/design-system/03 §2.2 + §3.2):
//   12px / weight 500 / fg.muted / letter-spacing 0 (= 装飾なし)
//   見出しブロック前後余白 = 8-16px
const Section = ({
  title,
  children,
}: {
  title: string
  children?: ReactNode
}) => (
  <Box>
    <Typography
      variant="caption"
      sx={{
        fontSize: 12,
        fontWeight: 500,
        color: 'text.secondary',
        display: 'block',
        mb: 0.75,
      }}
    >
      {title}
    </Typography>
    <Box>{children}</Box>
  </Box>
)

const InlineGenerating = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1, py: 0.5 }}>
    <PulsingDot size={8} />
    <Typography variant="body2" color="text.secondary">
      生成中…
    </Typography>
  </Box>
)

const InlineFailed = () => (
  <Typography variant="body2" color="error.main" sx={{ pl: 1, py: 0.5 }}>
    生成に失敗しました
  </Typography>
)

const InlineSkipped = () => (
  <Typography variant="body2" color="text.disabled" sx={{ pl: 1, py: 0.5 }}>
    NSIPS 未紐付けで生成不可
  </Typography>
)

const InlineMismatch = ({ generatedVersion }: { generatedVersion: string }) => (
  <Typography variant="body2" color="text.disabled" sx={{ pl: 1, py: 0.5 }}>
    この結果は表示できません (= {generatedVersion} で生成されており、 現在の設定
    (V3) と異なります)
  </Typography>
)

// V3 服薬指導準備 root. MeetingBlock と同 container contract (= Paper outlined +
// icon header + 区切り線 + body padding) で 2 block の視覚整合を取る.
//
// 構造 (上から):
//   1. 処方 (= 今回処方 + 前回からの差分)
//   2. 前回ヒアリング (= 症状 / 服薬状況 / 副作用 keyword)
//   3. 確認要点 (= 1+2 から LLM 導出)
//   ─────
//   4. 加算 (= 副次. 末尾)
//
// 初回指導 (= isInitial=true) は 1+2 を「初回指導」 1 行に集約.
//
// α のユーザーは見出しが「服薬指導｜監査」の切り替えになる (GuidancePrepCard.
// yakureki-back log/262 決定 1・log/267). β・ga は以下のサマリのまま.
export const GuidancePrepPaneV3 = ({ event }: Props) => {
  const live = useGuidanceQueryV3(event.id)
  const { isAlpha } = useReleaseChannel()

  const showRefresh = live.status === 'failed'

  if (isAlpha) {
    return (
      <GuidancePrepCard
        key={`prep-card-${event.id}`}
        event={event}
        live={live}
        pending={<PendingContent live={live} />}
        headerAction={
          showRefresh && <RefreshButton onClick={() => live.refetch()} />
        }
      />
    )
  }

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${colorTokens.border.default}`,
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: FONT_WEIGHT.semibold, flex: 1 }}
        >
          サマリ
        </Typography>
        {showRefresh && <RefreshButton onClick={() => live.refetch()} />}
      </Box>

      <Box sx={{ px: 2, py: 2 }}>
        <LiveContent event={event} live={live} />
      </Box>
    </Paper>
  )
}

const RefreshButton = ({ onClick }: { onClick: () => void }) => (
  <Tooltip title="再取得">
    <span>
      <IconButton
        size="small"
        onClick={onClick}
        aria-label="再取得"
        sx={{ p: 0.5 }}
      >
        <RefreshIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </span>
  </Tooltip>
)

// completed 以外の 1 行 (生成中… / 失敗 / 未紐付け / 版違い). ga のサマリと β のカードで共有.
const PendingContent = ({
  live,
}: {
  live: ReturnType<typeof useGuidanceQueryV3>
}) => {
  if (live.status === 'failed') return <InlineFailed />
  if (live.status === 'skipped') return <InlineSkipped />
  if (live.status === 'mismatch')
    return <InlineMismatch generatedVersion="v2" />
  return <InlineGenerating />
}

const LiveContent = ({
  event,
  live,
}: {
  event: YakurekiDetail
  live: ReturnType<typeof useGuidanceQueryV3>
}) => {
  if (live.status === 'processing' || live.status === null)
    return <InlineGenerating />
  if (live.status === 'failed') return <InlineFailed />
  if (live.status === 'skipped') return <InlineSkipped />
  if (live.status === 'mismatch')
    return <InlineMismatch generatedVersion="v2" />
  if (live.status === 'completed' && live.data) {
    return (
      <Body
        eventId={event.id}
        currentRx={event.current_prescription_data ?? undefined}
        previousRx={event.previous_prescription_data ?? undefined}
        data={live.data}
      />
    )
  }
  return null
}

const Body = ({
  eventId,
  currentRx,
  previousRx,
  data,
}: {
  eventId: string
  currentRx: YakurekiDetail['current_prescription_data'] | undefined
  previousRx: YakurekiDetail['previous_prescription_data'] | undefined
  data: GuidanceSupportDataV3
}) => {
  const isInitial = !!data.isInitial
  const prevKw = data.previousKeywords
  return (
    <Stack spacing={2}>
      {isInitial ? (
        <>
          <Section title="処方">
            <PrescriptionWithDiffPanel
              current={currentRx ?? null}
              previous={previousRx ?? null}
            />
          </Section>
          <Typography variant="body2" color="text.disabled">
            初回指導 (= 前回ヒアリング なし)
          </Typography>
        </>
      ) : (
        <>
          <Section title="処方">
            <PrescriptionWithDiffPanel
              current={currentRx ?? null}
              previous={previousRx ?? null}
            />
          </Section>
          {prevKw && (
            <Section title="前回ヒアリング">
              <PreviousKeywordsPanel data={prevKw} />
            </Section>
          )}
        </>
      )}

      <Section title="確認要点">
        <GuidanceCheckPointList points={data.checkPoints} />
        {/* トレーシングレポートのルール検知行 (NSIPS 受信時に確定済み・該当時のみ描画) */}
        <TracingReportPrepRow yakurekiId={eventId} />
      </Section>

      <Section title="加算">
        <KasanStatusPanel yakurekiId={eventId} />
      </Section>
    </Stack>
  )
}
